import React, { memo, useEffect, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import PlaceAutocomplete from '@/components/map/PlaceAutocomplete';
import FilterBox from '@/components/map/FilterBox';
import MapEditModal from '@/components/map/MapEditModal';
import { EditorInteractable } from '@/components/editor/EditorInteractable';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    selectAllMyMaps,
    selectMyMapsLoading,
    selectMyMapsError,
    setSelectedMapId,
    selectSelectedMapId,
    createCustomMap,
    updateCustomMapDetails,
    clearSelectedMap,
    selectAllPublicMaps,
    selectPublicMapsLoading,
    selectPublicMapsError,
    initializeMyMapsListener,
    initializePublicMapsListener,
    cleanupMapListeners,
    selectMyMapsListenerActive,
} from '@/store/slices/customMapsSlice';
import { selectUser, selectIsAuthenticated } from '@/store/slices/authSlice';
import { 
    setMapSelectionTriggeredByUrl,
    selectActiveAccordionItem,
    setActiveAccordionItem
} from '@/store/slices/mapUiSlice';
import { Eye, EyeOff, PenSquare, Share2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

// Memoize imported components
const MemoizedPlaceAutocomplete = memo(PlaceAutocomplete);
const MemoizedFilterBox = memo(FilterBox);

interface SidebarProps {
    locationsError: SerializedError | string | null;
    getErrorMessage: (error: SerializedError | string | null) => string;
    handleManualRefresh: () => void;
}

const MapSidebar: React.FC<SidebarProps> = ({
    locationsError,
    getErrorMessage,
    handleManualRefresh,
}) => {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const user = useAppSelector(selectUser);
    const [searchParams, setSearchParams] = useSearchParams();

    const myMaps = useAppSelector(selectAllMyMaps);
    const myMapsLoading = useAppSelector(selectMyMapsLoading);
    const myMapsError = useAppSelector(selectMyMapsError);
    const selectedMapId = useAppSelector(selectSelectedMapId);

    const [myMapsFilter, setMyMapsFilter] = useState('');
    const [newMapName, setNewMapName] = useState('');

    // Public Maps State
    const publicMaps = useAppSelector(selectAllPublicMaps);
    const publicMapsLoading = useAppSelector(selectPublicMapsLoading);
    const publicMapsError = useAppSelector(selectPublicMapsError);
    const [publicMapNameFilter, setPublicMapNameFilter] = useState('');
    const [publicMapCreatorFilter, setPublicMapCreatorFilter] = useState('');
    
    // Use the Redux state instead of local state for activeAccordionItem
    const activeAccordionItem = useAppSelector(selectActiveAccordionItem);
    
    // Dialog state for invalid map links
    const [invalidMapModalOpen, setInvalidMapModalOpen] = useState(false);
    const [invalidMapErrorMessage, setInvalidMapErrorMessage] = useState('');

    const [editingMapId, setEditingMapId] = useState<string | null>(null);

    const myMapsListenerActive = useAppSelector(selectMyMapsListenerActive);

    useEffect(() => {
        if (isAuthenticated && user?.id) {
            dispatch(initializeMyMapsListener(user.id));
        }
        dispatch(initializePublicMapsListener(user?.id || null));
        
        return () => {
            dispatch(cleanupMapListeners());
        };
    }, [isAuthenticated, user?.id, dispatch]);

    useEffect(() => {
        const sharedMapIdFromUrl = searchParams.get('shareMapId');
        if (sharedMapIdFromUrl) {
            console.log(`[Sidebar] Found shareMapId in URL: ${sharedMapIdFromUrl}`);
            
            // Check if maps are loaded before trying to select
            const isPublicMapsLoaded = publicMapsLoading === 'succeeded';
            const isMyMapsLoaded = myMapsLoading === 'succeeded';
            
            // Modified condition: Consider myMapsLoading 'succeeded' or myMaps array initialized as loaded
            // This ensures public maps can be loaded for new users even if they have no maps
            const myMapsReady = !isAuthenticated || isMyMapsLoaded || (myMaps && Array.isArray(myMaps));
            
            // Only proceed if maps are loaded
            if (isPublicMapsLoaded && myMapsReady) {
                console.log(`[Sidebar] Maps loaded. Checking for map: ${sharedMapIdFromUrl}`);
                
                // Check if the map exists in the user's own maps
                const isMyMap = isAuthenticated && isMyMapsLoaded && 
                                myMaps.some(map => map.id === sharedMapIdFromUrl);
                
                // Check if map exists in public maps
                const isPublicMap = publicMaps.some(map => map.id === sharedMapIdFromUrl);
                
                if (isMyMap) {
                    console.log(`[Sidebar] Map found in user's maps. Setting active.`);
                    dispatch(setSelectedMapId(sharedMapIdFromUrl));
                    dispatch(setActiveAccordionItem("my-maps")); // Open my maps section using Redux
                    dispatch(setMapSelectionTriggeredByUrl(true));
                } else if (isPublicMap) {
                    console.log(`[Sidebar] Map found in public maps. Setting active.`);
                    dispatch(setSelectedMapId(sharedMapIdFromUrl));
                    dispatch(setActiveAccordionItem("public-maps")); // Open public maps section using Redux
                    dispatch(setMapSelectionTriggeredByUrl(true));
                } else {
                    console.log(`[Sidebar] Warning: Map ${sharedMapIdFromUrl} not found or not accessible.`);
                    setInvalidMapErrorMessage("The shared map does not exist or you don't have permission to view it.");
                    setInvalidMapModalOpen(true);
                }
                
                // Always clean up URL parameter after processing to prevent reprocessing
                searchParams.delete('shareMapId');
                setSearchParams(searchParams);
            } else {
                console.log(`[Sidebar] Waiting for maps to load before selecting shared map. Public maps: ${isPublicMapsLoaded}, My maps ready: ${myMapsReady}`);
            }
        }
    // Only run on mount or if maps data/searchParams changes
    }, [searchParams, dispatch, publicMaps, myMaps, publicMapsLoading, myMapsLoading, isAuthenticated, setSearchParams]);

    useEffect(() => {
        // Log maps loading states whenever they change
        console.log(`[Sidebar] Maps Loading States - myMaps: ${myMapsLoading}, publicMaps: ${publicMapsLoading}`);
        console.log(`[Sidebar] Maps Data - myMaps count: ${myMaps.length}, publicMaps count: ${publicMaps.length}`);
        
        if (myMapsLoading === 'succeeded') {
            console.log(`[Sidebar] My maps successfully loaded with ${myMaps.length} maps`);
        }
        
        if (publicMapsLoading === 'succeeded') {
            console.log(`[Sidebar] Public maps successfully loaded with ${publicMaps.length} maps`);
        }
    }, [myMapsLoading, publicMapsLoading, myMaps.length, publicMaps.length]);

    // Add a new effect that ensures myMapsLoading reaches 'succeeded' state 
    // even for users who have no maps
    useEffect(() => {
        // Check if user is authenticated, listener is active, but myMapsLoading isn't 'succeeded'
        if (isAuthenticated && 
            myMapsListenerActive && 
            myMapsLoading !== 'succeeded' && 
            myMapsLoading !== 'pending' && 
            myMaps.length === 0) {
            console.log('[Sidebar] User has no maps but listener is active - dispatching loading succeeded');
            // This effect should only run once per session, when a new user first loads the map
            // It helps ensure that the myMapsLoading state is set to 'succeeded' even if there are no maps
            dispatch({ type: 'customMaps/fetchMyMaps/fulfilled', payload: [] });
        }
    }, [isAuthenticated, myMapsListenerActive, myMapsLoading, myMaps.length, dispatch]);

    const handleCreateNewMap = async () => {
        if (newMapName.trim() && user?.id && user?.displayName) {
            dispatch(createCustomMap({ name: newMapName.trim(), ownerId: user.id, ownerUsername: user.displayName }));
            setNewMapName('');
        }
    };

    const handleTogglePublic = (mapId: string, currentIsPublic: boolean) => {
        dispatch(updateCustomMapDetails({ mapId, isPublic: !currentIsPublic }));
        // Preserve the current accordion item - don't let it switch
        if (activeAccordionItem === "my-maps") {
            setTimeout(() => dispatch(setActiveAccordionItem("my-maps")), 0);
        }
    };
    
    const handleSelectMap = (mapId: string | null) => {
        if (selectedMapId === mapId) {
            dispatch(clearSelectedMap());
        } else if (mapId) {
            dispatch(setSelectedMapId(mapId));
        }
    };

    const filteredMyMaps = myMaps.filter(map => map.name.toLowerCase().includes(myMapsFilter.toLowerCase()));

    // Filter Public Maps
    const filteredPublicMaps = publicMaps.filter(map => 
        map.name.toLowerCase().includes(publicMapNameFilter.toLowerCase()) &&
        map.ownerUsername.toLowerCase().includes(publicMapCreatorFilter.toLowerCase())
    );

    const handleShareMap = (mapId: string, mapName: string): void => {
        const shareUrl = `${window.location.origin}${window.location.pathname}?shareMapId=${mapId}`;
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                alert(`Link for "${mapName}" copied to clipboard!`);
            })
            .catch(err => {
                console.error('Failed to copy map link: ', err);
                alert('Failed to copy link. See console for details.');
            });
    };

    return (
        <div className="z-10 flex flex-col gap-4 h-full">
            <MemoizedPlaceAutocomplete />
            <div className="flex-1 overflow-y-auto flex flex-col gap-4 px-2">
                {locationsError ? (
                    <div className="p-4 border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 rounded-md">
                        <h3 className="text-red-600 dark:text-red-400 font-medium mb-2">Loading Failed</h3>
                        <p className="text-sm text-red-600/80 dark:text-red-400/80 mb-3">
                            Unable to load location data. Error: {getErrorMessage(locationsError)}
                        </p>
                        <Button variant="outline" size="sm" onClick={handleManualRefresh} className="w-full">
                            Retry Loading
                        </Button>
                    </div>
                ) : (
                    <Accordion 
                        type="single" 
                        collapsible 
                        className="w-full" 
                        value={activeAccordionItem}
                        onValueChange={(value) => dispatch(setActiveAccordionItem(value))}
                    >
                        <AccordionItem value="filters" className="bg-background border border-border rounded-md px-4 mb-2">
                            <AccordionTrigger className="text-md">Filters</AccordionTrigger>
                            <AccordionContent>
                                <MemoizedFilterBox />
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="my-maps" className="bg-background border border-border rounded-md px-4 mb-2">
                            <AccordionTrigger className="text-md">My Maps</AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-1">
                                {isAuthenticated ? (
                                    <>
                                        {myMapsLoading === 'pending' && <p className="text-sm text-muted-foreground">Loading maps...</p>}
                                        {myMapsError && <p className="text-sm text-red-500">Error: {myMapsError}</p>}
                                        {myMapsLoading === 'succeeded' && filteredMyMaps.length === 0 && myMapsFilter && (
                                            <p className="text-sm text-muted-foreground">No maps match your filter.</p>
                                        )}
                                        
                                        <div className="h-60 overflow-y-auto flex flex-col gap-1 pr-1 mb-2 border border-border rounded-md p-1">
                                            {filteredMyMaps.map(map => (
                                                <div key={map.id} 
                                                    className={`p-1 rounded-md cursor-pointer border flex items-center justify-between relative
                                                                ${selectedMapId === map.id 
                                                                    ? 'bg-primary/10 border-primary' 
                                                                    : 'hover:bg-muted/50 border-transparent'
                                                                }`}
                                                    onClick={() => handleSelectMap(map.id)}
                                                    title={`${map.name} (${map.ownerUsername})${map.description ? `\n\n${map.description}` : ''}`}
                                                >
                                                    <div className="flex items-center flex-grow overflow-hidden">
                                                        <span className="text-xs bg-muted text-muted-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                                            {map.locationIds.length}
                                                        </span>
                                                        <p className="font-medium text-sm truncate pr-2 ml-2">{map.name}</p>
                                                    </div>

                                                    {/* Icon container: Reserves space for up to 3 icons, aligns them to the right */}
                                                    <div className="flex items-center justify-end gap-0.5 flex-shrink-0 w-[88px]">
                                                        {/* Edit and Share icons - only for selected map */}
                                                        {selectedMapId === map.id && (
                                                            <>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon" 
                                                                    className="h-7 w-7"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setEditingMapId(map.id);
                                                                    }}
                                                                    title="Edit Map"
                                                                >
                                                                    <PenSquare className="h-4 w-4" />
                                                                </Button>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon" 
                                                                    className="h-7 w-7"
                                                                    onClick={(e) => { 
                                                                        e.stopPropagation(); 
                                                                        handleShareMap(map.id, map.name);
                                                                    }}
                                                                    title={map.isPublic ? "Share Map" : "Make map public to share"}
                                                                    disabled={!map.isPublic}
                                                                >
                                                                    <Share2 className="h-4 w-4" />
                                                                </Button>
                                                            </>
                                                        )}

                                                        {/* Eye Icon Button - always present, always toggles public status */}
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className={`h-7 w-7 flex-shrink-0 transition-opacity
                                                                ${map.isPublic ? 'opacity-100' : 'opacity-30 hover:opacity-60 focus-within:opacity-100'}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // Always toggle the public status of this specific map
                                                                handleTogglePublic(map.id, map.isPublic);
                                                            }}
                                                            title={map.isPublic ? "Make Private" : "Make Public"}
                                                        >
                                                            {map.isPublic ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                                                        </Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex gap-2">
                                            <Input 
                                                placeholder="Filter your maps..." 
                                                value={myMapsFilter} 
                                                onChange={(e) => setMyMapsFilter(e.target.value)} 
                                            />
                                        </div>
                                        <div className="flex gap-2 mt-2">
                                            <Input 
                                                placeholder="New map name..." 
                                                value={newMapName} 
                                                onChange={(e) => setNewMapName(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleCreateNewMap()} 
                                            />
                                            <Button onClick={handleCreateNewMap} size="sm" disabled={!newMapName.trim() || !user?.id}>
                                                Create
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-sm text-muted-foreground">Log in to see your maps.</p>
                                )}
                            </AccordionContent>
                        </AccordionItem>
                        <AccordionItem value="public-maps" className="bg-background border border-border rounded-md px-4">
                            <AccordionTrigger className="text-md">Public Maps</AccordionTrigger>
                            <AccordionContent className="flex flex-col gap-2">
                                {/* Filter inputs will be moved from here */}

                                {publicMapsLoading === 'pending' && <p className="text-sm text-muted-foreground">Loading public maps...</p>}
                                {publicMapsError && <p className="text-sm text-red-500">Error: {publicMapsError}</p>}
                                
                                <div className="h-60 overflow-y-auto flex flex-col gap-1 pr-1 border border-border rounded-md p-1 mb-2">
                                    {publicMapsLoading === 'succeeded' && filteredPublicMaps.length === 0 && !publicMapNameFilter && !publicMapCreatorFilter && (
                                        <p className="text-sm text-muted-foreground p-2 text-center">No public maps available currently.</p>
                                    )}
                                    {publicMapsLoading === 'succeeded' && filteredPublicMaps.length === 0 && (publicMapNameFilter || publicMapCreatorFilter) && (
                                        <p className="text-sm text-muted-foreground p-2 text-center">No public maps match your filters.</p>
                                    )}
                                    {filteredPublicMaps.map(map => (
                                        <div key={map.id} 
                                            className={`p-1 rounded-md cursor-pointer border relative
                                                        ${selectedMapId === map.id 
                                                            ? 'bg-primary/10 border-primary' 
                                                            : 'hover:bg-muted/50 border-transparent'
                                                        }`}
                                            onClick={() => handleSelectMap(map.id)}
                                            title={`${map.name} (${map.ownerUsername})${map.description ? `\n\n${map.description}` : ''}`}
                                        >
                                            <EditorInteractable
                                                entityId={map.id}
                                                entityType="customMap"
                                                className="w-full flex items-center justify-between"
                                                requiredPermission="editor"
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <div className="flex items-center overflow-hidden">
                                                        <span className="text-xs bg-muted text-muted-foreground rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                                                            {map.locationIds.length}
                                                        </span>
                                                        <p className="font-medium text-sm truncate pr-2 ml-2">{map.name}</p>
                                                    </div>

                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7 flex-shrink-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleShareMap(map.id, map.name);
                                                        }}
                                                        title="Share this map"
                                                    >
                                                        <Share2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </EditorInteractable>
                                        </div>
                                    ))}
                                </div>
                                {/* Moved filter inputs here and changed their layout */}
                                <div className="flex flex-col gap-2">
                                    <Input 
                                        placeholder="Filter by map name..." 
                                        value={publicMapNameFilter} 
                                        onChange={(e) => setPublicMapNameFilter(e.target.value)} 
                                    />
                                    <Input 
                                        placeholder="Filter by creator..." 
                                        value={publicMapCreatorFilter} 
                                        onChange={(e) => setPublicMapCreatorFilter(e.target.value)} 
                                    />
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                )}
            </div>
            
            <MapEditModal
                mapId={editingMapId}
                isOpen={!!editingMapId}
                onClose={() => setEditingMapId(null)}
            />
            
            {/* Dialog for invalid map links */}
            <Dialog open={invalidMapModalOpen} onOpenChange={setInvalidMapModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Map Not Available</DialogTitle>
                        <DialogDescription>
                            {invalidMapErrorMessage}
                        </DialogDescription>
                    </DialogHeader>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default memo(MapSidebar); 